import { Formik, Form, Field, ErrorMessage, FormikProps } from 'formik'
import * as Yup from 'yup'
import { LanguageAutocompleteField } from '../../profile/components/fields/LanguageAutocompleteField'
import { useEffect, useRef, useState } from 'react'
import SocialMediaModal from '../../profile/components/SocialMediaModal'
import { KTSVG, toAbsoluteUrl } from '../../../../_metronic/helpers'
import { updateProfile, updateProfileImg } from '../../../services/profileAdminService'
import { useHistory } from 'react-router-dom'
import { useUserProfileContext } from '../../../context/UserProfileContext'
import { RequiredInfoModal } from '../../../helpers/RequiredInfoModal'
import { showErrorAlert } from '../../../helpers/alert'
import ImageField from '../../teamAdmin/components/fields/ImageField'
import { useIntroduction } from '../../../hooks/introduction/useIntroduction'
import { exampleImages } from '../../../utils/constants'
import { updateProfilePicture } from '../../../services/membersAdminService'
import { imageOrUrlSchema } from '../../../utils/UtilsFiles'

type SocialMediaItem = {
  id: string
  name: string
  icon: string
  url: string
}

const ProfileAdminForm: React.FC = () => {
  const navigate = useHistory()
  const [socialMediaItems, setSocialMediaItems] = useState<SocialMediaItem[]>([])
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
  const [isModalOpen, setModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedCards, setSelectedCards] = useState<string[]>([])
  const MAX_LENGTH: number = 150
  const [countLength, setCountLength] = useState<number>(0)
  const { data, refetch, searchParams } = useUserProfileContext()
  const { collection } = useIntroduction()
  const formikRef = useRef<FormikProps<any>>(null)
  const [showModal, setShowModal] = useState(false);
  const [messageModal, setMessageModal] = useState("");
  const [introductions, setIntroductions] = useState<any[]>([])
  const [step, setStep] = useState<number>(1)

  useEffect(() => {
    if(collection?.length > 0) {
      setIntroductions(collection)
      if (collection && collection?.length>0 && data?.introduction && data?.introduction?.length > 0) {
        let ids: string[] = [];
        collection.map((i: any) => {
          if (data?.introduction.includes(i?.title)) {
            ids.push(i.id);
          }
        });
        setSelectedCards(ids);
      }
    }
  }, [collection])

  useEffect(() => {
    if (data?.languages && data.languages.length > 0) {
      setSelectedLanguages(data.languages)
    }
    if (data?.socialMediaProfiles && data.socialMediaProfiles.length > 0) {
      const iconMap: Record<string, string> = {
        Website: '/media/svg/nobilis/web.svg',
        Linkedin: '/media/svg/nobilis/lk.svg',
        Instagram: '/media/svg/nobilis/instagram.svg',
        Facebook: '/media/svg/nobilis/fb-black.svg',
        'X Profile': '/media/svg/nobilis/x.svg',
        Twitter: '/media/svg/nobilis/x.svg',
      }

      const idMap: Record<string, string> = {
        Website: 'website',
        Linkedin: 'linkedin',
        Instagram: 'instagram',
        Facebook: 'facebook',
        'X Profile': 'twitter',
        Twitter: 'twitter',
      }

      const mappedSocialMedia: SocialMediaItem[] = data.socialMediaProfiles.map(
        (profile, index) => ({
          id: idMap[profile.platformName] || `social-${index}`,
          name: profile.platformName,
          icon: iconMap[profile.platformName] || '/media/svg/nobilis/web.svg',
          url: profile.profileUrl,
        })
      )

      setSocialMediaItems(mappedSocialMedia)
    }
  }, [data])

  const handleSocialMediaSelect = (option: any) => {
    let isAdd = (socialMediaItems ?? []).filter((i) => i.name == option.name);
    if (isAdd?.length > 0) {
      showErrorAlert({ title: 'Duplicate Social Network', message: `You've already added this social network.` })
    } else {
      const newItem: SocialMediaItem = {
        id: option.id,
        name: option.name,
        icon: option.icon,
        url: '',
      }
      setSocialMediaItems([...socialMediaItems, newItem])
    }
  }

  const handleSocialMediaUrlChange = (id: string, url: string) => {
    setSocialMediaItems((items) => items.map((item) => (item.id === id ? { ...item, url } : item)))
  }

  const handleRemoveSocialMedia = (id: string) => {
    setSocialMediaItems((items) => items.filter((item) => item.id !== id))
  }

  const toggleCard = (id: string) => {
    setSelectedCards((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }

  const focusFirstError = () => {
    // validamos los campos de formik
    const errors = formikRef.current?.errors
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0]
      setMessageModal(`Please check the field error '${String(firstErrorField).replaceAll('_', ' ')}' to save your progress.`);
      const el = document.querySelector(
        `[name="${firstErrorField}"]`
      ) as HTMLElement;
      setShowModal(true);
      el?.focus()
      return;
    }
    // validamos los campos que no estan en el formik
    const haveError = socialMediaItems.filter((item) => !item.url || item.url.trim() === '')
    if (haveError.length > 0) {
      setMessageModal('Please enter the social media URL to save your progress.')
      setShowModal(true)

      // Espera a que el DOM esté listo (modal / re-render)
      setTimeout(() => {
        const inputs = Array.from(document.querySelectorAll<HTMLInputElement>('.social-media-input-field'))
        const firstEmptyInput = inputs.find((input) => !input.value || input.value.trim() === '')
        firstEmptyInput?.focus()
      }, 0);
      return
    }
    // si no hay error enviamos el form
    formikRef.current?.submitForm()
  }

  return (
    <>
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={{
          alias_title: data?.aliasTitle ?? '',
          name: data?.firstName ?? '',
          surname: data?.surname ?? '',
          introduction_headline: data?.introductionHeadline ?? '',
          residence: data?.postalAddress ?? '',
          guiding_principle: data?.guidingPrinciple ?? '',
          annual_limits_introduction: data?.annualLimitsIntroduction.toString(),
          receive_reports: data?.receiveReports.toString() ?? 'false',
          profile_picture: data?.profilePicture ?? null,
        }}
        validationSchema={Yup.object({
          alias_title: Yup.string().max(50, "Max 50 characters"),
          name: Yup.string().required('This field is required'),
          surname: Yup.string().required('This field is required'),
          introduction_headline: Yup.string().max(220, "Max 220 characters"),
          residence: Yup.string().max(220, "Max 220 characters"),
          guiding_principle: Yup.string().max(MAX_LENGTH, `Max ${MAX_LENGTH} characters`),
          annual_limits_introduction: Yup.number().required('Select an annual limit'),
          receive_reports: Yup.boolean().required('Select an option'),
          profile_picture: imageOrUrlSchema(),
        })}
        onSubmit={async (values: any, actions: any) => {
          if (step === 1) {
            setStep(2);
            actions.setSubmitting(false);
            return;
          }

          try {
            setLoading(true)

            const payload = {
              ...values,
              postal_address: values?.residence ?? '',
              introductions: selectedCards.map((i) => Number(i)),
              languages: selectedLanguages,
              social_media_profiles: socialMediaItems.map((item) => ({
                platform_name: item.name,
                profile_url: item.url,
              })),
              first_name: values.name,
              last_name: values.surname,
            }

            let response = await updateProfile(
              payload,
              !!searchParams.userSelected ? searchParams.userSelected : ''
            )

            let msg = 'Your information has been saved successfully.'
            if (values.profile_picture && values.profile_picture instanceof File) {
              let isUpdateImg = await updateProfileImg(values.profile_picture, data?.user)
              if(isUpdateImg.status == 'ok') {
                msg = 'Your information and profile picture have been saved successfully.'
              }else{
                msg = 'Your information has been saved successfully, but there was an issue saving your profile picture. Please try updating your profile picture again.'
              }
            }

            if (response?.firstName) {
              showErrorAlert({
                title: 'Saved',
                message: msg,
              })
              await refetch()
              navigate.goBack()
            }

            setLoading(false)
          } catch (error: any) {
            setLoading(false)
            showErrorAlert({
              title: 'Unable to update profile',
              message: "We couldn't save your changes. Please review your information and try again.",
            })
          }
        }}
      >
        {({ values, setFieldValue, validateForm, setTouched }) => (
          <Form className='profile-admin-form profile-form-base'>

            {step === 1 && (
              <div className='profile-admin-form  profile-form-base'>
                {/* Title */}
                <div className='form-header'>
                  <h2 className='form-header__title'>Edit Your Profile</h2>
                </div>

                {/* Section: General Information */}
                <div className='form-section'>
                  <h3 className='section-title'>General Information</h3>

                  <div className='form-field'>
                    <label className='field-label'>Alias / Title (if applicable)</label>
                    <Field name='alias_title' className='input' />
                    <div className='field-hint'>
                      Pseudonym, Royal, Academic, Religious, or other title
                    </div>
                    <ErrorMessage name="alias_title" component="div" className="tap-add-experience-text text-danger" />
                  </div>

                  <div className='form-row'>
                    <div className='form-field'>
                      <label className='field-label'>Name</label>
                      <Field name='name' className='input  input-text-style' />
                      <ErrorMessage name='name' component="div" className="tap-add-experience-text text-danger" />
                    </div>
                    <div className='form-field'>
                      <label className='field-label'>Surname</label>
                      <Field name='surname' className='input' />
                      <ErrorMessage name='surname' component="div" className="tap-add-experience-text text-danger" />
                    </div>
                  </div>

                  <div className='form-field'>
                    <label className='field-label'>Headline</label>
                    <Field name='introduction_headline' className='input' />
                    <div className='field-hint'>
                      List the most significant titles that define you (e.g., Entrepreneur, CEO, Actor,
                      Author, Philanthropist, F1 Driver, Nobel Laureate, Mother of four). Be specific.
                    </div>
                    <ErrorMessage name="introduction_headline" component="div" className="tap-add-experience-text text-danger" />
                  </div>

                  <div className='form-field'>
                    <label className='field-label'>Primary Residence</label>
                    <Field name='residence' className='input' />
                    <ErrorMessage name="residence" component="div" className="tap-add-experience-text text-danger" />
                  </div>

                  <div className='form-field'>
                    <label className='field-label'>Language spoken</label>
                    <LanguageAutocompleteField
                      values={selectedLanguages}
                      onChange={setSelectedLanguages}
                    />
                  </div>
                  <div className='profile-form-group social-media-group'>
                    <label>Social Media & Online Presence</label>
                    {socialMediaItems.map((item) => (
                      <div key={item.id} className='social-media-input'>
                        <div className='social-media-input-content'>
                          <KTSVG path={toAbsoluteUrl(item.icon)} className='social-media-input-icon' />
                          <input
                            type='text'
                            placeholder={`Add your ${item.name.toLowerCase()} profile URL`}
                            value={item.url}
                            onChange={(e) => handleSocialMediaUrlChange(item.id, e.target.value)}
                            className='social-media-input-field'
                          />
                          <button type="button" onClick={() => handleRemoveSocialMedia(item.id)} className="remove-btn tap-flex-center">
                            <KTSVG path='/media/svg/nobilis/teams_and_partner/team_and_partner_experiences_add_experience_availability_date_delete.svg' />&nbsp;
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Botón para agregar */}
                    <div className='social-media-add-btn' onClick={() => setModalOpen(true)}>
                      {socialMediaItems.length > 0 ? '+ Add Another' : '+ Add'}
                    </div>

                    <SocialMediaModal
                      isOpen={isModalOpen}
                      onClose={() => setModalOpen(false)}
                      onSelect={handleSocialMediaSelect}
                    />
                  </div>
                </div>

                {/* Section: Guiding Principle */}
                <div className='guiding-principle-field'>
                  <label className='gp-label'>Guiding Principle</label>
                  <Field
                    name='guiding_principle'
                    maxLength={MAX_LENGTH}
                    onChange={(e: any) => {
                      const value = e.target.value
                      setFieldValue('guiding_principle', value)
                      setCountLength(value.length)
                    }}
                    className='gp-input'
                  />
                  <div className='gp-hint-row'>
                    <small className='gp-hint'>
                      In one sentence, please share the principle or belief that drives you.
                    </small>
                    <small className='gp-counter'>
                      {countLength > 0
                        ? `${values.guiding_principle.length} / ${MAX_LENGTH}`
                        : `${values.guiding_principle.length} / ${MAX_LENGTH}`}{' '}
                      characters
                    </small>
                  </div>
                  <ErrorMessage name="guiding_principle" component="div" className="tap-add-experience-text text-danger" />
                </div>

                {/* Section: Introduction management */}
                <div className='intro-mgmt'>
                  <h3 className='intro-mgmt__title'>Introduction Management</h3>
                  <p className='intro-mgmt__desc'>
                    At Nobilis, introductions are thoughtfully curated to foster meaningful, high-value
                    relationships. Our platform uses a combination of intelligent algorithms and human
                    review to facilitate connections that are aligned with your interests, values, and
                    status.
                    <br />
                    <br />
                    General Introductions are available to all members by default. Please indicate any
                    additional areas in which you are open to receiving curated connections.
                  </p>

                  <p className='intro-mgmt__hint cursor-pointer'>Select all that apply</p>

                  <div className='intro-mgmt__cards'>
                    {introductions.map((card) => (
                      <div
                        key={card.id}
                        className={`intro-mgmt__card ${selectedCards.includes(card.id) ? 'is-selected' : ''
                          }`}
                        onClick={() => toggleCard(card.id)}
                      >
                        <div className='intro-mgmt__card-title'>{card.title}</div>
                        <div className='intro-mgmt__card-desc'>{card.description}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Section: Annual Limits */}
                <div className='annual-limits'>
                  <h3 className='annual-limits__title'>Annual Limits</h3>

                  <p className='annual-limits__desc'>
                    Choose to limit the number of introduction requests you receive annually
                  </p>

                  <div className='annual-limits__options'>
                    <label className='annual-limits__radio'>
                      <Field type='radio' name='annual_limits_introduction' value='0' />
                      No Limit
                    </label>
                    <label className='annual-limits__radio'>
                      <Field type='radio' name='annual_limits_introduction' value='5' />
                      Up to 5 introductions
                    </label>
                    <label className='annual-limits__radio'>
                      <Field type='radio' name='annual_limits_introduction' value='10' />
                      Up to 10 introductions
                    </label>
                  </div>

                  <p className='annual-limits__desc'>
                    Would you like to receive quarterly reports summarizing introduction requests that
                    were declined?
                  </p>
                  <ErrorMessage name="annual_limits_introduction" component="div" className="tap-add-experience-text text-danger" />

                  <div className='annual-limits__options'>
                    <label className='annual-limits__radio'>
                      <Field type='radio' name='receive_reports' value='true' />
                      Yes
                    </label>
                    <label className='annual-limits__radio'>
                      <Field type='radio' name='receive_reports' value='false' />
                      No
                    </label>
                  </div>
                  <ErrorMessage name="receive_reports" component="div" className="tap-add-experience-text text-danger" />
                </div>
              </div>
            )}

            {step === 2 && (
              <>
                <div className='form-header'>
                  <h2 className='form-header__title'>Edit Your Profile</h2>
                </div>
                <div className="team-admin-add-member__field">
                  <label>Photo</label>
                  <ImageField />
                  <ErrorMessage name="profile_picture" component="div" className="text-danger" />
                </div>
                {/* Examples Section */}
                <div className='photo-examples-section mt-5'>
                  <div className='examples-group'>
                    <label className='examples-label'>Example</label>
                    <div className={`examples-images`}>
                      {exampleImages.map((src, index) => (
                        <div key={index} className={`example-image ${index === 1 ? 'bw' : ''}`}>
                          <img src={toAbsoluteUrl(src)} alt={`Example ${index + 1}`} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Requirements */}
                  <div className='photo-requirements'>
                    <div className='requirements-left'>
                      <div className='requirement-row'>
                        <div className='check-icon'>✓</div>
                        <span>High-resolution, professional headshot</span>
                      </div>
                      <div className='requirement-row'>
                        <div className='check-icon'>✓</div>
                        <span>Clear focus on face</span>
                      </div>
                      <div className='requirement-row'>
                        <div className='check-icon'>✓</div>
                        <span>Neutral or calm background with proper lighting</span>
                      </div>
                    </div>

                    <div className='requirements-right'>
                      <div className='requirement-row'>
                        <div className='check-icon'>✓</div>
                        <span>Recent image</span>
                      </div>
                      <div className='requirement-row'>
                        <div className='check-icon'>✓</div>
                        <span>Format: JPEG/PNG, max 5MB</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* BOTONES */}
            <div className='form-actions'>
              <button type='button' className='btn-secondary' onClick={() => {
                  if (step === 2) {
                    setStep(1)
                  } else {
                    navigate.goBack()
                  }
                }}
              >
                {step === 2 ? 'Back' : 'Cancel'}
              </button>

              {step === 1 ? (
                <button type='button' className='btn nb-btn-primary' onClick={async (e) => {
                  e.preventDefault();
                  const errors: any = await validateForm();
                  setTouched({
                    name: true,
                    surname: true,
                    alias_title: true,
                    residence: true,
                    guiding_principle: true,
                    annual_limits_introduction: true,
                    receive_reports: true,
                  });
                  const step1FieldList = [
                    'name',
                    'surname',
                    'alias_title',
                    'residence',
                    'guiding_principle',
                    'annual_limits_introduction',
                    'receive_reports',
                  ];
                  const hasStep1Errors = step1FieldList.some((f) => Boolean(errors[f]));
                  if (!hasStep1Errors) {
                    setStep(2);
                  }
                }}>
                  Next
                  <img src='/media/svg/nobilis/vector1.svg' alt='' className='nb-btn-icon nb-btn-icon--white'/>
                </button>
              ) : (
                <button type='submit' className='btn nb-btn-primary' disabled={loading}>
                  Save Changes
                  <img src='/media/svg/nobilis/vector1.svg' alt='' className='nb-btn-icon nb-btn-icon--white'/>
                </button>
              )}
            </div>

          </Form>
        )}
      </Formik>

      <RequiredInfoModal open={showModal} message={messageModal} onClose={() => setShowModal(false)} />
    </>
  )
}

export default ProfileAdminForm